import React, { FC, useEffect, useMemo, useState } from "react";
import { Checkbox, Description, Dropdown, Flex, FlexSpan, Icon, Switch } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { AttributeDefinitionDc } from "@evergis/api";

import { RestrictedWidthContainer } from "components/Dialogs/DialogComponents/styled";

import {
  AttributeItem,
  AttributeItemCol,
  AttributeItemIcon,
  AttributeItemRightCol,
  AttributesBlockContainer,
  AttributeWarning,
} from "../styled";

import { useLayerAttributes } from "../../../../../../hooks/layer/usePostgresLayer";
import { getFieldTypeIcon, getFieldTypeLangKey } from "../../../../../../evergis/core/attributes/fieldTypes";
import { useToggle } from "../../../../../../hooks/useToggle";

import { LayerCopyField, Step1Props } from "../types";

// eslint-disable-next-line max-lines-per-function
export const Step1: FC<Step1Props> = ({ layerInfo, targetLayer, setCopyFields }) => {
  const { t } = useTranslation("dialogs");
  const sourceAttributes: { [key: string]: AttributeDefinitionDc } = useLayerAttributes(layerInfo?.name);
  const targetAttributes: { [key: string]: AttributeDefinitionDc } = useLayerAttributes(targetLayer?.name);
  const [allAvailable, setAllAvailable] = useState<boolean>(true);
  const [fields, setFields] = useState<LayerCopyField[]>([]);
  const [showPairs, toggleShowPairs] = useToggle(false);

  const targetAttributeOptions = useMemo(
    () =>
      targetAttributes
        ? Object.entries(targetAttributes).reduce(
            (acc, [name, item]) => [
              ...acc,
              { value: name, text: item.alias || name, icon: getFieldTypeIcon(item.type, item.subType) },
            ],
            [],
          )
        : [],
    [targetAttributes],
  );

  const reservedTargetFields = useMemo(
    () => fields.filter(({ targetField }) => !!targetField).map(({ targetField }) => targetField),
    [fields],
  );

  useEffect(() => {
    if (!sourceAttributes || !targetAttributes) {
      return;
    }

    const initial = Object.entries(sourceAttributes).reduce((acc, [name, item]) => {
      return targetAttributes[name] !== undefined && targetAttributes[name].type === item.type
        ? [...acc, { sourceField: name, targetField: name, sourceAttribute: item, enabled: true }]
        : [...acc, { sourceField: name, targetField: void 0, sourceAttribute: item, enabled: false }];
    }, []);

    setFields(initial);
  }, [sourceAttributes, targetAttributes]);

  useEffect(() => {
    setCopyFields(fields);
  }, [fields, setCopyFields]);

  return (
    <AttributesBlockContainer>
      <RestrictedWidthContainer>
        <>
          <Flex style={{ position: "relative", top: "-1rem", alignItems: "baseline", justifyContent: "space-between" }}>
            <FlexSpan style={{ paddingLeft: "1.5rem", width: "70%" }}>
              <Checkbox
                checked={allAvailable}
                onChange={() => {
                  setAllAvailable(!allAvailable);
                  setFields(
                    allAvailable
                      ? fields.map(item => ({ ...item, enabled: false }))
                      : fields.map(item => ({ ...item, enabled: true })),
                  );
                }}
                label={t("copyResource.allAvailableAttributes", { ns: "dialogs" })}
              />
              <div style={{ marginLeft: "1.75rem" }}>
                <Description>{t("copyResource.attributesCopyMessage", { ns: "dialogs" })}</Description>
              </div>
            </FlexSpan>
            <FlexSpan>
              <Switch
                checked={showPairs}
                label={
                  (
                    <Description>{t("copyResource.showToBeCopiedOnly", { ns: "dialogs" })}</Description>
                  ) as unknown as string
                }
                onChange={toggleShowPairs}
              />
            </FlexSpan>
          </Flex>
          {fields.map(({ sourceField, targetField, enabled, sourceAttribute }) => {
            const reservedFields = reservedTargetFields.filter(field => field !== targetField);
            const availableByTypeOptions = targetAttributeOptions.filter(
              ({ value }) => targetAttributes[value].type === sourceAttribute.type,
            );
            const attributeOptions = availableByTypeOptions.filter(({ value }) => !reservedFields.includes(value));
            const isAttributePair = !!targetField;
            const isUnavailable = attributeOptions.length === 0 && !isAttributePair;

            return (
              ((showPairs && isAttributePair) || !showPairs) && (
                <AttributeItem key={sourceField}>
                  <AttributeItemCol>
                    <div style={{ marginRight: "1rem" }}>
                      <Checkbox
                        checked={enabled}
                        onChange={() => {
                          setFields(
                            fields.map(item =>
                              item.sourceField === sourceField ? { ...item, enabled: !enabled } : item,
                            ),
                          );
                        }}
                      />
                    </div>
                    <div>
                      <div>{sourceAttribute.alias || sourceField}</div>
                      <div>
                        <Description style={{ margin: 0 }}>
                          {t(getFieldTypeLangKey(sourceAttribute.type, sourceAttribute.subType), { ns: "common" })}
                        </Description>
                      </div>
                    </div>
                  </AttributeItemCol>
                  <AttributeItemRightCol>
                    <AttributeItemIcon>
                      {!isUnavailable ? <Icon kind="arrow_forward" /> : <Icon kind="warning" error />}
                    </AttributeItemIcon>
                    {!isUnavailable ? (
                      <Dropdown
                        value={targetField || null}
                        label={t("copyResource.targetAttribute")}
                        placeholder={t("copyResource.chooseAttribute")}
                        width={"23.5rem"}
                        zIndex={120}
                        options={[{ value: null, text: t("notChosen", { ns: "common" }) }, ...attributeOptions]}
                        onChange={([option]) =>
                          setFields(
                            fields.map(item =>
                              item.sourceField === sourceField
                                ? {
                                    ...item,
                                    targetField: option.value,
                                    enabled: targetField || option.value === null ? item.enabled : true,
                                  }
                                : item,
                            ),
                          )
                        }
                      />
                    ) : (
                      <AttributeWarning>
                        {t("copyResource.unavailableAttributeMessage", { ns: "dialogs" })}
                      </AttributeWarning>
                    )}
                  </AttributeItemRightCol>
                </AttributeItem>
              )
            );
          })}
        </>
      </RestrictedWidthContainer>
    </AttributesBlockContainer>
  );
};
